import { useMemo } from 'react';
import { t } from '@lingui/core/macro';
import { Calendar, ChevronDown, X } from 'lucide-react';
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  Box,
  MenuItem,
  Divider,
  Input,
  Text,
} from '@chakra-ui/react';
import { djs } from '@duke-hq/djs';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';

export default function SemesterDueDateFilter() {
  const { updateFilters, filters } = useFiltersContext();

  const options = useMemo(
    () => [
      {
        label: t`All overdue`,
        dueDateEnd: djs().startOf('day').format('YYYY-MM-DD'),
      },
      {
        label: t`Recently due (past 14 days)`,
        dueDateStart: djs().subtract(14, 'days').format('YYYY-MM-DD'),
        dueDateEnd: djs().startOf('day').format('YYYY-MM-DD'),
      },
      {
        label: t`Due soon (next 14 days)`,
        dueDateStart: djs().startOf('day').format('YYYY-MM-DD'),
        dueDateEnd: djs().add(14, 'days').format('YYYY-MM-DD'),
      },
    ],
    [],
  );

  const menuLabel = useMemo(() => {
    if (filters.dueDateStart !== undefined && filters.dueDateEnd !== undefined) {
      return `${djs(filters.dueDateStart).format('DD/MM/YYYY')} - ${djs(filters.dueDateEnd).format('DD/MM/YYYY')}`;
    } else if (filters.dueDateStart !== undefined) {
      return `${t`From`}: ${djs(filters.dueDateStart).format('DD/MM/YYYY')}`;
    } else if (filters.dueDateEnd !== undefined) {
      return `${t`To`}: ${djs(filters.dueDateEnd).format('DD/MM/YYYY')}`;
    }

    return t`Due by`;
  }, [filters.dueDateEnd, filters.dueDateStart]);

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="ghost"
        width="auto"
        colorScheme="gray"
        px={2}
        rightIcon={
          filters.dueDateStart !== undefined || filters.dueDateEnd !== undefined ? (
            <Box
              minW="fit-content"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                updateFilters({ dueDateStart: undefined, dueDateEnd: undefined });
              }}
              ml={1}
            >
              <X size="16px" />
            </Box>
          ) : (
            <ChevronDown size="16px" />
          )
        }
        leftIcon={<Calendar size="16px" />}
      >
        {menuLabel}
      </MenuButton>
      <MenuList>
        {options.map(({ label, dueDateStart, dueDateEnd }) => (
          <MenuItem
            key={label}
            onClick={() => {
              updateFilters({ dueDateStart, dueDateEnd });
            }}
          >
            {label}
          </MenuItem>
        ))}

        <Divider my={2} />

        <Box px={3}>
          <Text fontSize="2xs" mr={2}>
            From:
          </Text>
          <Input
            type="date"
            value={filters.dueDateStart ?? ''}
            onChange={(e) => updateFilters({ dueDateStart: e.target.value || undefined })}
            flex="1"
            max={filters.dueDateEnd ?? ''}
          />

          <Text fontSize="2xs" mr={2} mt={2}>
            To:
          </Text>
          <Input
            type="date"
            value={filters.dueDateEnd ?? ''}
            onChange={(e) => updateFilters({ dueDateEnd: e.target.value || undefined })}
            flex="1"
            min={filters.dueDateStart ?? ''}
          />
        </Box>
      </MenuList>
    </Menu>
  );
}
